import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { translate } from '../../../base/i18n/functions';
import './ConversationHistoryList.css';
const ConversationHistoryList = ({ t, dispatch }) => {
    const [conversations, setConversations] = useState([]);
    const [filteredConversations, setFilteredConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentFilter, setCurrentFilter] = useState('all');
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [currentView, setCurrentView] = useState('list');
    useEffect(() => {
        loadConversations();
    }, []);
    useEffect(() => {
        applyFilters();
    }, [conversations, searchTerm, currentFilter]);
    const loadConversations = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch('/deepai/api/user-conversations');
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const conversationList = await response.json();
            // 各会話の詳細情報を取得
            const enrichedConversations = await Promise.all(conversationList.map(async (conv) => {
                try {
                    const detailResponse = await fetch(`/deepai/api/get-conversation/${conv.id}`);
                    if (detailResponse.ok) {
                        const messages = await detailResponse.json();
                        return {
                            ...conv,
                            messages,
                            messageCount: messages.length,
                            lastMessage: messages[messages.length - 1],
                            hasAudio: messages.some((msg) => msg.audio_path)
                        };
                    }
                    return {
                        ...conv,
                        messages: [],
                        messageCount: 0,
                        hasAudio: false
                    };
                }
                catch (error) {
                    console.error(`Error loading conversation ${conv.id}:`, error);
                    return {
                        ...conv,
                        messages: [],
                        messageCount: 0,
                        hasAudio: false
                    };
                }
            }));
            setConversations(enrichedConversations);
        }
        catch (error) {
            console.error('Error loading conversations:', error);
            setError('会話履歴の読み込みに失敗しました');
        }
        finally {
            setLoading(false);
        }
    };
    const applyFilters = () => {
        let filtered = conversations.filter(conv => {
            // 検索フィルタ
            if (searchTerm) {
                const searchMatch = conv.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (conv.lastMessage && conv.lastMessage.message.toLowerCase().includes(searchTerm.toLowerCase()));
                if (!searchMatch)
                    return false;
            }
            // カテゴリフィルタ
            switch (currentFilter) {
                case 'recent':
                    const oneWeekAgo = new Date();
                    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
                    return new Date(conv.timestamp) > oneWeekAgo;
                case 'audio':
                    return conv.hasAudio;
                case 'all':
                default:
                    return true;
            }
        });
        // 最新順でソート
        filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setFilteredConversations(filtered);
    };
    const handleConversationClick = (conversation) => {
        setSelectedConversation(conversation);
        setCurrentView('detail');
    };
    const handleBackToList = () => {
        setCurrentView('list');
        setSelectedConversation(null);
    };
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    const formatPreview = (message, maxLength = 100) => {
        return message.length > maxLength
            ? message.substring(0, maxLength) + '...'
            : message;
    };
    if (currentView === 'detail' && selectedConversation) {
        return (React.createElement(ConversationDetail, { conversation: selectedConversation, onBack: handleBackToList, t: t }));
    }
    return (React.createElement("div", { className: "conversation-history-container" },
        React.createElement("div", { className: "conversation-history-header" },
            React.createElement("h2", { className: "conversation-history-title" }, "\uD83D\uDCAC \u5BFE\u8A71\u5C65\u6B74\u4E00\u89A7"),
            React.createElement("p", { className: "conversation-history-subtitle" }, "\u904E\u53BB\u306EAI\u3068\u306E\u4F1A\u8A71\u3092\u78BA\u8A8D\u3067\u304D\u307E\u3059")),
        React.createElement("div", { className: "conversation-controls" },
            React.createElement("div", { className: "search-section" },
                React.createElement("input", { className: "search-input", type: "text", placeholder: "\u4F1A\u8A71\u3092\u691C\u7D22...\uFF08\u30BF\u30A4\u30C8\u30EB\u3001\u5185\u5BB9\u3067\u691C\u7D22\uFF09", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value) })),
            React.createElement("div", { className: "filter-section" },
                React.createElement("button", { className: `filter-button ${currentFilter === 'all' ? 'active' : ''}`, onClick: () => setCurrentFilter('all') }, "\u3059\u3079\u3066"),
                React.createElement("button", { className: `filter-button ${currentFilter === 'recent' ? 'active' : ''}`, onClick: () => setCurrentFilter('recent') }, "\u6700\u8FD1"),
                React.createElement("button", { className: `filter-button ${currentFilter === 'audio' ? 'active' : ''}`, onClick: () => setCurrentFilter('audio') }, "\u97F3\u58F0\u3042\u308A"))),
        React.createElement("div", { className: "conversation-list" },
            loading && (React.createElement("div", { className: "loading-state" },
                React.createElement("div", { className: "loading-icon" }, "\uD83D\uDCCA"),
                React.createElement("p", null, "\u4F1A\u8A71\u5C65\u6B74\u3092\u8AAD\u307F\u8FBC\u307F\u4E2D..."))),
            error && (React.createElement("div", { className: "error-state" },
                React.createElement("div", { className: "error-icon" }, "\u274C"),
                React.createElement("h3", null, "\u30A8\u30E9\u30FC\u304C\u767A\u751F\u3057\u307E\u3057\u305F"),
                React.createElement("p", null, error),
                React.createElement("button", { onClick: loadConversations, className: "retry-button" }, "\u518D\u8A66\u884C"))),
            !loading && !error && filteredConversations.length === 0 && (React.createElement("div", { className: "empty-state" },
                React.createElement("div", { className: "empty-icon" }, "\uD83D\uDCAD"),
                React.createElement("h3", null, "\u4F1A\u8A71\u5C65\u6B74\u304C\u898B\u3064\u304B\u308A\u307E\u305B\u3093"),
                React.createElement("p", null, "\u691C\u7D22\u6761\u4EF6\u3092\u5909\u66F4\u3059\u308B\u304B\u3001\u65B0\u3057\u3044\u4F1A\u8A71\u3092\u958B\u59CB\u3057\u3066\u304F\u3060\u3055\u3044"))),
            !loading && !error && filteredConversations.map(conversation => (React.createElement("div", { key: conversation.id, className: "conversation-item", onClick: () => handleConversationClick(conversation) },
                React.createElement("div", { className: "conversation-main" },
                    React.createElement("div", { className: "conversation-title-row" },
                        React.createElement("span", { className: "conversation-title" }, conversation.title),
                        conversation.hasAudio && (React.createElement("span", { className: "audio-indicator" }, "\uD83C\uDFB5"))),
                    React.createElement("div", { className: "conversation-meta" },
                        "\uD83D\uDCC5 ",
                        formatDate(conversation.timestamp)),
                    conversation.lastMessage && (React.createElement("div", { className: "conversation-preview" }, formatPreview(conversation.lastMessage.message)))),
                React.createElement("div", { className: "conversation-stats" },
                    React.createElement("div", { className: "message-count" },
                        conversation.messageCount || 0,
                        "\u4EF6"),
                    React.createElement("div", { className: "last-updated" },
                        "\u6700\u7D42\u66F4\u65B0: ",
                        new Date(conversation.timestamp).toLocaleDateString('ja-JP')))))))));
};
// 対話詳細コンポーネント
const ConversationDetail = ({ conversation, onBack, t }) => {
    const [messages, setMessages] = useState(conversation.messages || []);
    const [currentFilter, setCurrentFilter] = useState('all');
    const [filteredMessages, setFilteredMessages] = useState(messages);
    const [audioPlayers, setAudioPlayers] = useState(new Map());
    const [playingAudioId, setPlayingAudioId] = useState(null);
    useEffect(() => {
        setFilteredMessages(messages.filter(msg => {
            switch (currentFilter) {
                case 'text':
                    return msg.action === 'text';
                case 'nod':
                    return msg.action === 'nod';
                case 'audio':
                    return msg.audio_path;
                case 'all':
                default:
                    return true;
            }
        }));
    }, [messages, currentFilter]);
    const toggleAudio = async (messageId, audioPath) => {
        const audioKey = `audio-${messageId}`;
        let audio = audioPlayers.get(audioKey);
        if (!audio) {
            // 音声ファイルのパスを修正
            let fullAudioPath;
            // パスのパターンを確認
            if (audioPath.startsWith('http://') || audioPath.startsWith('https://')) {
                // 完全なURLの場合
                fullAudioPath = audioPath;
            }
            else if (audioPath.startsWith('/deepai/')) {
                // すでに/deepai/プレフィックスがある場合
                fullAudioPath = audioPath;
            }
            else if (audioPath.startsWith('uploads/')) {
                // uploadsから始まる場合は/deepai/を先頭に追加
                fullAudioPath = `/deepai/${audioPath}`;
            }
            else if (audioPath.startsWith('/uploads/')) {
                // /uploadsから始まる場合は/deepai/を先頭に追加
                fullAudioPath = `/deepai${audioPath}`;
            }
            else if (audioPath.startsWith('/')) {
                // /deepai/プレフィックスを追加
                fullAudioPath = `/deepai${audioPath}`;
            }
            else {
                // 相対パスの場合
                fullAudioPath = `/deepai/${audioPath}`;
            }
            console.log('Loading audio from:', fullAudioPath);
            audio = new Audio();
            audio.preload = 'auto';
            audio.src = fullAudioPath;
            // CORS対応
            audio.crossOrigin = 'anonymous';
            // エラーハンドリング
            audio.addEventListener('error', (e) => {
                console.error('Audio load error:', e);
                const audioElement = e.currentTarget;
                const error = audioElement.error;
                let errorMessage = '音声ファイルの読み込みに失敗しました';
                if (error) {
                    switch (error.code) {
                        case MediaError.MEDIA_ERR_ABORTED:
                            errorMessage += ': 再生が中断されました';
                            break;
                        case MediaError.MEDIA_ERR_NETWORK:
                            errorMessage += ': ネットワークエラー';
                            break;
                        case MediaError.MEDIA_ERR_DECODE:
                            errorMessage += ': デコードエラー';
                            break;
                        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                            errorMessage += ': サポートされていないファイル形式';
                            break;
                    }
                }
                alert(`${errorMessage}: ${fullAudioPath}`);
            });
            audio.addEventListener('loadeddata', () => {
                console.log('Audio loaded successfully:', fullAudioPath);
            });
            audio.addEventListener('ended', () => {
                setPlayingAudioId(null);
            });
            setAudioPlayers(prev => new Map(prev).set(audioKey, audio));
        }
        try {
            if (audio.paused) {
                // 他のオーディオを停止
                audioPlayers.forEach((otherAudio, key) => {
                    if (key !== audioKey && !otherAudio.paused) {
                        otherAudio.pause();
                    }
                });
                await audio.play();
                setPlayingAudioId(messageId);
            }
            else {
                audio.pause();
                setPlayingAudioId(null);
            }
        }
        catch (error) {
            console.error('Audio playback error:', error);
            alert('音声の再生に失敗しました。音声ファイルが存在するか確認してください。');
        }
    };
    const formatTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString('ja-JP', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };
    const getActionBadge = (action) => {
        const types = {
            'text': 'テキスト',
            'nod': '相槌',
            'store': '保留',
            'other': 'その他'
        };
        return types[action] || action;
    };
    return (React.createElement("div", { className: "conversation-detail" },
        React.createElement("div", { className: "detail-header" },
            React.createElement("button", { className: "back-button", onClick: onBack }, "\u2190 \u4E00\u89A7\u306B\u623B\u308B"),
            React.createElement("div", { className: "detail-title" },
                React.createElement("h2", null, conversation.title),
                React.createElement("div", { className: "detail-meta" },
                    React.createElement("span", null,
                        "\uD83D\uDCC5 ",
                        new Date(conversation.timestamp).toLocaleDateString('ja-JP')),
                    React.createElement("span", null,
                        "\uD83D\uDCAC ",
                        messages.length,
                        "\u4EF6\u306E\u30E1\u30C3\u30BB\u30FC\u30B8"),
                    conversation.hasAudio && React.createElement("span", null, "\uD83C\uDFB5 \u97F3\u58F0\u3042\u308A")))),
        React.createElement("div", { className: "detail-controls" },
            React.createElement("button", { className: `filter-button ${currentFilter === 'all' ? 'active' : ''}`, onClick: () => setCurrentFilter('all') }, "\u3059\u3079\u3066"),
            React.createElement("button", { className: `filter-button ${currentFilter === 'text' ? 'active' : ''}`, onClick: () => setCurrentFilter('text') }, "\u30C6\u30AD\u30B9\u30C8\u306E\u307F"),
            React.createElement("button", { className: `filter-button ${currentFilter === 'nod' ? 'active' : ''}`, onClick: () => setCurrentFilter('nod') }, "\u76F8\u69CC"),
            React.createElement("button", { className: `filter-button ${currentFilter === 'audio' ? 'active' : ''}`, onClick: () => setCurrentFilter('audio') }, "\u97F3\u58F0\u3042\u308A")),
        React.createElement("div", { className: "messages-container" }, filteredMessages.length === 0 ? (React.createElement("div", { className: "empty-state" },
            React.createElement("div", { className: "empty-icon" }, "\uD83D\uDCAD"),
            React.createElement("p", null, "\u8A72\u5F53\u3059\u308B\u30E1\u30C3\u30BB\u30FC\u30B8\u304C\u898B\u3064\u304B\u308A\u307E\u305B\u3093"))) : (filteredMessages.map(message => (React.createElement("div", { key: message.id, className: `message ${message.sender.toLowerCase() === 'user' ? 'user' : 'ai'}` },
            React.createElement("div", { className: "message-header" },
                React.createElement("div", { className: "message-sender" }, message.sender.toLowerCase() === 'user' ? '👤 あなた' : '🤖 AIアシスタント'),
                React.createElement("div", { className: "message-time" }, formatTime(message.timestamp))),
            React.createElement("div", { className: "message-content" }, message.message),
            React.createElement("div", { className: "message-actions" },
                React.createElement("span", { className: `action-badge ${message.action}` }, getActionBadge(message.action))),
            message.audio_path && (React.createElement("div", { className: "audio-player" },
                React.createElement("button", { className: "audio-play-button", onClick: () => toggleAudio(message.id, message.audio_path) }, playingAudioId === message.id ? '⏸️' : '▶️'),
                React.createElement("span", { className: "audio-info" }, "\uD83C\uDFB5 \u97F3\u58F0\u518D\u751F"))))))))));
};
export default translate(connect()(ConversationHistoryList));
